import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import LiveDriversTracking from "./LiveDriversTracking";
import { useDrafts } from "../../contexts/DraftsContext";
import {
  Car,
  Plus,
  X,
  Trash2,
  Loader2,
  Camera,
  Truck,
  Phone,
  User as UserIcon,
  CheckCircle2,
  AlertCircle,
  Edit2,
  Hash,
} from "lucide-react";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ──────────── Photo Upload Box ────────────
const PhotoBox = ({ label, value, onChange, testId }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handlePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload-vehicle-photo`, fd);
      onChange(data.path);
      toast.success("تم رفع الصورة");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل رفع الصورة");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <p className="text-[11px] font-bold text-[#5C6E64] mb-1 text-center">{label}</p>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        data-testid={testId}
        className={`w-full aspect-square rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden bg-[#F8FAFC] hover:bg-[#EFF6FC] ${value ? "border-[#3B82F6]" : "border-[#E4EBE6]"}`}
      >
        {busy ? (
          <Loader2 className="w-6 h-6 animate-spin text-[#3B82F6]" />
        ) : value ? (
          <img src={`${API}/files/${value}`} alt={label} className="w-full h-full object-cover" />
        ) : (
          <div className="flex flex-col items-center gap-1 text-[#5C6E64]">
            <Camera className="w-7 h-7" />
            <span className="text-[10px]">رفع صورة</span>
          </div>
        )}
      </button>
      <input ref={inputRef} type="file" accept="image/*" onChange={handlePick} className="hidden" />
      {value && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-[10px] text-[#E85D45] mt-1 hover:underline w-full text-center"
        >
          حذف الصورة
        </button>
      )}
    </div>
  );
};

// ──────────── Vehicle Form Modal ────────────
const VehicleFormModal = ({ vehicle, drivers, onClose, onSaved }) => {
  const isEdit = !!vehicle?.id;
  const [name, setName] = useState(vehicle?.name || "");
  const [plate, setPlate] = useState(vehicle?.plate || "");
  const [driverId, setDriverId] = useState(vehicle?.driver_id || "");
  const [photoPlate, setPhotoPlate] = useState(vehicle?.photo_plate || null);
  const [photoVehicle, setPhotoVehicle] = useState(vehicle?.photo_vehicle || null);
  const [photoId, setPhotoId] = useState(vehicle?.photo_id || null);
  const [note, setNote] = useState(vehicle?.note || "");
  const [saving, setSaving] = useState(false);
  const { requestClose } = useDrafts();

  const hasContent = !isEdit && !!(name.trim() || plate.trim() || driverId || photoPlate || photoVehicle || photoId || note.trim());
  const guardedClose = () => requestClose(hasContent, isEdit ? "تعديل مركبة" : "إضافة مركبة جديدة", onClose);

  // Drivers already linked to another vehicle should not appear (except current)
  const availableDrivers = useMemo(() => drivers, [drivers]);

  const submit = async () => {
    if (!name.trim()) { toast.error("اكتب اسم/رقم المركبة"); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        plate: plate.trim(),
        driver_id: driverId || null,
        photo_plate: photoPlate,
        photo_vehicle: photoVehicle,
        photo_id: photoId,
        note: note.trim(),
      };
      if (isEdit) {
        await axios.patch(`${API}/admin/vehicles/${vehicle.id}`, payload);
        toast.success("تم تحديث المركبة");
      } else {
        await axios.post(`${API}/admin/vehicles`, payload);
        toast.success("تم إضافة المركبة");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الحفظ");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[125] bg-black/60 flex items-center justify-center p-3" dir="rtl" data-testid="vehicle-form-modal" onClick={guardedClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[94vh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white p-4 flex justify-between items-center shrink-0">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <Car className="w-5 h-5" /> {isEdit ? "تعديل مركبة" : "إضافة مركبة جديدة"}
          </h3>
          <button onClick={guardedClose} data-testid="vehicle-form-close" className="bg-white/20 hover:bg-white/30 rounded-full p-1.5">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-2">
            <Field label="اسم المركبة *">
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="vehicle-name"
                placeholder="مثال: تويوتا هايلكس"
                className="w-full px-3 py-2.5 rounded-xl border-2 border-[#3B82F6] focus:outline-none" />
            </Field>
            <Field label="رقم اللوحة">
              <input value={plate} onChange={(e) => setPlate(e.target.value)} data-testid="vehicle-plate" dir="ltr"
                placeholder="بغداد ١٢٣٤٥"
                className="w-full px-3 py-2.5 rounded-xl border-2 border-[#E4EBE6] focus:outline-none focus:border-[#3B82F6]" />
            </Field>
          </div>
          <Field label="السائق المسؤول">
            <select value={driverId} onChange={(e) => setDriverId(e.target.value)} data-testid="vehicle-driver"
              className="w-full px-3 py-2.5 rounded-xl border-2 border-[#E4EBE6] focus:outline-none focus:border-[#3B82F6] bg-white">
              <option value="">— بدون سائق —</option>
              {availableDrivers.map((d) => (
                <option key={d.id} value={d.id}>{d.full_name} · {d.phone}</option>
              ))}
            </select>
          </Field>
          <div>
            <p className="text-xs font-bold text-[#5C6E64] mb-2">صور المركبة</p>
            <div className="grid grid-cols-3 gap-2">
              <PhotoBox label="لوحة الرقم" value={photoPlate} onChange={setPhotoPlate} testId="vehicle-photo-plate" />
              <PhotoBox label="المركبة" value={photoVehicle} onChange={setPhotoVehicle} testId="vehicle-photo-vehicle" />
              <PhotoBox label="هوية السائق" value={photoId} onChange={setPhotoId} testId="vehicle-photo-id" />
            </div>
          </div>
          <Field label="ملاحظة">
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="vehicle-note"
              className="w-full px-3 py-2.5 rounded-xl border-2 border-[#E4EBE6] focus:outline-none focus:border-[#3B82F6]" />
          </Field>
        </div>
        <div className="p-3 border-t-2 border-[#E4EBE6] flex justify-end gap-2 shrink-0">
          <button onClick={guardedClose} className="bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-2 px-4 rounded-full">
            إلغاء
          </button>
          <button onClick={submit} disabled={saving} data-testid="vehicle-form-submit"
            className="bg-[#7ED321] hover:bg-[#6cb91d] disabled:bg-[#BAE6FD] text-white font-bold py-2 px-5 rounded-full flex items-center gap-1">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {isEdit ? "حفظ" : "إضافة"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-bold text-[#5C6E64] block mb-1">{label}</label>
    {children}
  </div>
);

// ──────────── Main Tab ────────────
const VehiclesTab = () => {
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | vehicle obj

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/vehicles`),
        axios.get(`${API}/admin/drivers`),
      ]);
      setVehicles(r1.data || []);
      setDrivers(r2.data || []);
    } catch { toast.error("فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const removeVehicle = async (v) => {
    if (!(await confirmDialog(`حذف المركبة "${v.name}"؟`))) return;
    try {
      await axios.delete(`${API}/admin/vehicles/${v.id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
  };

  const toggleStatus = async (v) => {
    const next = v.status === "available" ? "busy" : "available";
    try {
      await axios.patch(`${API}/admin/vehicles/${v.id}`, { status: next });
      toast.success(next === "available" ? "صارت متاحة" : "صارت غير متاحة");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل"); }
  };

  const totals = useMemo(() => ({
    count: vehicles.length,
    available: vehicles.filter((v) => v.status === "available").length,
    busy: vehicles.filter((v) => v.status === "busy").length,
  }), [vehicles]);

  return (
    <div className="space-y-3" data-testid="vehicles-tab" dir="rtl">
      <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <Car className="w-5 h-5" />
          <h3 className="font-bold text-lg">المركبات</h3>
        </div>
        <p className="text-xs opacity-90">كل مركبة مرتبطة بسائق ثابت. الحالة تتغيّر تلقائيًا عند تعيين فاتورة، ويعود السائق ليُحدّد "متاح" بعد إنجاز كل فواتيره.</p>
        <div className="grid grid-cols-3 gap-2 mt-3 text-center">
          <Stat label="إجمالي المركبات" value={totals.count} />
          <Stat label="متاحة الآن" value={totals.available} color="#A7F3D0" />
          <Stat label="غير متاحة" value={totals.busy} color="#FCA5A5" />
        </div>
      </div>

      {/* Live tracking embedded map — hidden while add/edit form is open */}
      {!editing && <LiveDriversTracking />}

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setEditing("new")} data-testid="add-vehicle-btn"
          className="bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold py-2.5 px-4 rounded-full flex items-center gap-1">
          <Plus className="w-4 h-4" /> إضافة مركبة
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10"><Loader2 className="w-8 h-8 animate-spin inline text-[#1E3A5F]" /></div>
      ) : vehicles.length === 0 ? (
        <div className="text-center text-[#5C6E64] py-12 bg-white rounded-2xl border-2 border-dashed border-[#E4EBE6]">
          <Car className="w-10 h-10 mx-auto mb-2 opacity-60" />
          <p className="text-sm">لا توجد مركبات. أضف أول مركبة.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {vehicles.map((v) => {
            const isBusy = v.status === "busy";
            return (
              <div key={v.id} data-testid={`vehicle-card-${v.id}`}
                className={`bg-white rounded-2xl p-3 border-2 ${isBusy ? "border-[#FCA5A5]" : "border-[#86efac]"} shadow-sm`}>
                <div className="flex justify-between items-start gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Truck className={`w-5 h-5 ${isBusy ? "text-[#E85D45]" : "text-[#15803d]"}`} />
                    <div className="min-w-0">
                      <p className="font-bold text-[#1E3A5F] truncate">{v.name}</p>
                      {v.plate && <p className="text-[11px] text-[#5C6E64] flex items-center gap-1"><Hash className="w-3 h-3" /><span dir="ltr">{v.plate}</span></p>}
                    </div>
                  </div>
                  <span className={`text-[11px] font-bold px-2 py-1 rounded-full ${isBusy ? "bg-[#FEE2E2] text-[#E85D45]" : "bg-[#DCFCE7] text-[#15803d]"}`}>
                    {isBusy ? "غير متاح" : "متاح الآن"}
                  </span>
                </div>

                {/* Photos preview */}
                {(v.photo_plate || v.photo_vehicle || v.photo_id) && (
                  <div className="grid grid-cols-3 gap-1 mb-2">
                    {[{src: v.photo_plate, label: "اللوحة"}, {src: v.photo_vehicle, label: "المركبة"}, {src: v.photo_id, label: "الهوية"}].map((p, i) =>
                      p.src ? (
                        <a key={i} href={`${API}/files/${p.src}`} target="_blank" rel="noreferrer" className="block">
                          <img src={`${API}/files/${p.src}`} alt={p.label} className="w-full h-16 object-cover rounded-lg" />
                        </a>
                      ) : <div key={i} className="w-full h-16 rounded-lg bg-[#F3F6F4] flex items-center justify-center text-[10px] text-[#5C6E64]">—</div>
                    )}
                  </div>
                )}

                {v.driver_id ? (
                  <div className="bg-[#F8FAFC] rounded-xl p-2 mb-2 text-xs">
                    <p className="flex items-center gap-1"><UserIcon className="w-3 h-3" /> <span className="font-bold">{v.driver_name || "—"}</span></p>
                    {v.driver_phone && <p className="flex items-center gap-1 text-[#5C6E64]"><Phone className="w-3 h-3" /> <span dir="ltr">{v.driver_phone}</span></p>}
                    {Number(v.active_orders || 0) > 0 && (
                      <p className="flex items-center gap-1 text-[#E85D45] mt-1"><AlertCircle className="w-3 h-3" /> فواتير نشطة: <span dir="ltr" className="font-bold">{v.active_orders}</span></p>
                    )}
                  </div>
                ) : (
                  <div className="bg-[#FFF7ED] border border-[#FDBA74] rounded-xl p-2 mb-2 text-[11px] text-[#9A3412] flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> لا يوجد سائق مرتبط
                  </div>
                )}

                <div className="flex gap-2">
                  <button onClick={() => toggleStatus(v)} data-testid={`toggle-vehicle-${v.id}`}
                    className={`flex-1 text-white text-xs font-bold py-1.5 rounded-full flex items-center justify-center gap-1 ${isBusy ? "bg-[#15803d] hover:bg-[#126b32]" : "bg-[#E85D45] hover:bg-[#d54d35]"}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> {isBusy ? "تبديل لمتاح" : "تبديل لغير متاح"}
                  </button>
                  <button onClick={() => setEditing(v)} data-testid={`edit-vehicle-${v.id}`}
                    className="bg-[#3B82F6] hover:bg-[#2563eb] text-white text-xs font-bold py-1.5 px-3 rounded-full flex items-center gap-1">
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => removeVehicle(v)} data-testid={`del-vehicle-${v.id}`}
                    className="bg-[#FEE2E2] hover:bg-[#FECACA] text-[#E85D45] text-xs font-bold py-1.5 px-2 rounded-full flex items-center justify-center">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <VehicleFormModal
          vehicle={editing === "new" ? null : editing}
          drivers={drivers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

const Stat = ({ label, value, color }) => (
  <div className="bg-white/15 rounded-xl py-2">
    <p className="text-[10px] opacity-90">{label}</p>
    <p className="font-bold text-lg" style={{ color: color || "#fff" }} dir="ltr">{value}</p>
  </div>
);

export default VehiclesTab;
